import { List, Clock, BookOpen, Tag, Bookmark } from 'lucide-react';
import { formatDate } from './DateFormatter';
import { useMarkBlogAsFavorite } from '../../functions/blogHook';
import '../../index.css';

interface Section {
  id: string;
  title: string;
}

interface Post {
  id: string; // Se añade un ID para identificar el blog
  author: string;
  readTime: string;
  date: string;
  authorSpecialty: string;
  category: string;
  tags: string[];
  authorName: string;
}

interface SidebarProps {
  contentSections: Section[];
  post: Post;
  scrollToSection: (id: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ contentSections, post, scrollToSection }) => {
  const { mutate: markAsFavorite, isLoading } = useMarkBlogAsFavorite();

  return (
    <aside className="w-64 bg-white border-r hidden md:block  lg:block scrollbar-hidden border-gray-200 flex-shrink-0 overflow-y-auto">
      <div className="p-4 space-y-8">
        {/* Content Navigation */}
        <div>
          <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
            <List className="w-5 h-5 mr-2 text-gray-700" />
            <h3 className="font-semibold text-gray-900">Content</h3>
          </div>
          <nav className="space-y-1">
            {contentSections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors flex items-center"
              >
                <span className="w-2 h-2 bg-gray-300 rounded-full mr-3"></span>
                {section.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Author Section */}
        <div>
          <div className="mb-4 pb-2 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Author</h3>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm text-gray-500">CS</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {post.authorName}
              </p>
              <p className="text-xs text-gray-500">{post.authorSpecialty}</p>
            </div>
          </div>
        </div>

        {/* Article Info */}
        <div>
          <div className="mb-4 pb-2 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Article Info</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center text-sm text-gray-600 space-x-3 bg-gray-50 p-3 rounded-lg">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span>{post.readTime} Min Read</span>
            </div>
            
            <div className="flex items-center text-sm text-gray-600 space-x-3 bg-gray-50 p-3 rounded-lg">
              <BookOpen className="w-4 h-4 flex-shrink-0" />
              <span>{formatDate(post.date)}</span>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <Tag className="w-4 h-4 mr-2" />
                <span className="font-medium">Categories</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 text-xs bg-white border border-gray-200 rounded-full">
                  {post.category}
                </span>
                <span className="px-2 py-1 text-xs bg-white border border-gray-200 rounded-full">
                  Technology
                </span>
              </div>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <Tag className="w-4 h-4 mr-2" />
                <span className="font-medium">Tags</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-2 py-1 text-xs bg-white border border-gray-200 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div>
          <div className="mb-4 pb-2 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Actions</h3>
          </div>
          <button
            onClick={() => markAsFavorite(post.id)}
            disabled={isLoading}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 
            bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Bookmark className="w-4 h-4" />
            <span>{isLoading ? 'Saving...' : 'Save Article'}</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
